import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Check, Camera, FileText, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MAGIC_MIRROR, waLink } from "@/data/site";
import MasonryGallery from "@/components/MasonryGallery";
import CTASection from "@/components/CTASection";
import ComboSection from "@/components/ComboSection";
import MagicMirrorReservation from "@/components/MagicMirrorReservation";
import { SERVICE } from "@/constants/testIds";

const MagicMirror = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative h-[75vh] md:h-[85vh] overflow-hidden">
        <img
          src={MAGIC_MIRROR.hero}
          alt="Magic Mirror Fotobooth in actie"
          className="absolute inset-0 w-full h-full object-cover object-center animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/40" />
        <div className="relative z-10 h-full flex flex-col justify-center container-x">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="eyebrow text-gold mb-4"
          >
            Magic Mirror Fotobooth
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="font-serif text-white text-6xl md:text-7xl lg:text-8xl leading-[1.02] max-w-3xl"
          >
            Maak van jouw feest
            <br />
            <span className="italic text-gold">een herinnering voor altijd.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-6 text-white/80 max-w-xl leading-relaxed"
          >
            {MAGIC_MIRROR.intro}
          </motion.p>
        </div>
      </section>

      {/* USPs — 6 pillars */}
      <section className="bg-ink text-white py-20 md:py-24">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow text-gold mb-3">Waarom de Magic Mirror</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">
              Verrassend. Uniek. <span className="italic text-gold">Fun.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
            {MAGIC_MIRROR.usps.map((u, i) => {
              const Icon = Icons[u.icon] || Camera;
              return (
                <motion.div
                  key={u.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="text-center"
                >
                  <Icon size={30} className="text-gold mx-auto mb-5" strokeWidth={1.2} />
                  <p className="font-serif text-xl md:text-2xl text-white mb-3">{u.title}</p>
                  <p className="text-sm text-white/60 leading-relaxed max-w-xs mx-auto">{u.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Manifesto quote */}
      <section
        className="relative py-32 md:py-44 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/3171837/pexels-photo-3171837.jpeg?auto=compress&cs=tinysrgb&w=2000)",
        }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="relative z-10 container-narrow max-w-4xl text-center"
        >
          <span className="font-serif text-gold text-6xl md:text-7xl leading-none block mb-6">"</span>
          <p className="font-serif italic text-white text-2xl md:text-4xl lg:text-5xl leading-tight">
            {MAGIC_MIRROR.manifesto}
          </p>
          <div className="mt-10 flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-gold" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-gold">Magic Mirror by Lenslopers</span>
            <span className="h-px w-10 bg-gold" />
          </div>
        </motion.div>
      </section>

      {/* Customization — on-brand personalization */}
      <section className="bg-beige py-24 md:py-32">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-6 md:order-2"
          >
            <div className="relative aspect-[4/5] overflow-hidden shadow-2xl">
              <img
                src={MAGIC_MIRROR.customization.image}
                alt="Custom branded Magic Mirror foto"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute -bottom-4 -left-4 hidden md:block bg-gold text-white px-6 py-3">
                <span className="font-script text-2xl leading-none">100% op maat</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-6 md:order-1"
          >
            <p className="eyebrow mb-4">{MAGIC_MIRROR.customization.eyebrow}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-ink">
              {MAGIC_MIRROR.customization.title.split("jouw")[0]}
              <span className="italic text-gold">jouw{MAGIC_MIRROR.customization.title.split("jouw")[1]}</span>
            </h2>
            <p className="mt-8 text-ink/70 leading-relaxed">
              {MAGIC_MIRROR.customization.text}
            </p>
            <ul className="mt-8 space-y-3">
              {MAGIC_MIRROR.customization.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-ink/80">
                  <Check size={16} className="text-gold mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Inclusief */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-narrow grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-6">
            <p className="eyebrow mb-3">Magic Mirror Fotobooth</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Alles <span className="italic text-gold">inbegrepen.</span>
            </h2>
            <p className="font-serif text-3xl mt-8 text-gold">{MAGIC_MIRROR.priceFrom}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                data-testid="magic-mirror-reserve-scroll-btn"
                href="#reserveren"
                className="btn-gold"
              >
                Reserveer je datum
              </a>
              <a
                data-testid={`${SERVICE.bookBtn}-mirror-info`}
                href={waLink("Hallo Eric, ik wil graag info over de Magic Mirror voor mijn feest.")}
                target="_blank"
                rel="noreferrer"
                className="btn-outline-dark"
              >
                Vraag vrijblijvend info
              </a>
            </div>
          </div>
          <div className="md:col-span-6">
            <ul className="space-y-3">
              {MAGIC_MIRROR.inclusief.map((f) => (
                <li key={f} className="flex items-start gap-3 text-neutral-800">
                  <Check size={16} className="text-gold mt-1 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <p className="eyebrow mt-10 mb-4">Altijd een succes op</p>
            <div className="flex flex-wrap gap-2">
              {MAGIC_MIRROR.occasions.map((o) => (
                <span key={o} className="px-3 py-1.5 border border-neutral-300 text-xs uppercase tracking-[0.15em]">
                  {o}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}

      {/* Combos */}
      <ComboSection ids={MAGIC_MIRROR.combos} title="Combineer voor het maximale effect" />

      {/* Gallery */}
      <MasonryGallery filterable={false} initialTag="magic-mirror" />

      {/* FAQ */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-narrow">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="eyebrow mb-3">Veelgestelde vragen</p>
            <h2 className="font-serif text-4xl md:text-5xl">Alles wat je wilt weten</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {MAGIC_MIRROR.faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-neutral-200">
                <AccordionTrigger className="font-serif text-xl md:text-2xl text-left hover:text-gold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-600 text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Reservatieformulier */}
      <MagicMirrorReservation />

      {/* Verhuurvoorwaarden */}
      <section className="bg-beige py-16 md:py-20">
        <div className="container-narrow">
          <Link
            to="/verhuurvoorwaarden-magic-mirror"
            data-testid="magic-mirror-verhuurvoorwaarden-link"
            className="group flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white border-l-2 border-gold p-7 md:p-9 transition-shadow hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.35)]"
          >
            <div className="flex items-start gap-5">
              <FileText size={22} className="text-gold mt-1 shrink-0" />
              <div>
                <p className="font-serif text-xl md:text-2xl text-ink leading-tight">
                  Lees de verhuurvoorwaarden
                </p>
                <p className="mt-2 text-ink/60 text-[15px] leading-relaxed max-w-xl">
                  Alles over reservatie, waarborg, opbouw, stroomvoorziening en gebruik van de
                  Magic Mirror — helder en op één pagina.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink/70 group-hover:text-gold transition-colors shrink-0">
              Bekijken
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </span>
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default MagicMirror;
